import { supabase } from '@/lib/supabase/client';
import {
  mergeLocationCampaignImproveItems,
  resolveCompletedAtForCampaignImproveStatus,
  type CampaignImproveDerivationContext,
  type CampaignImproveItemRow,
} from '@/lib/customer-ops/campaign-improve';
import type {
  CampaignImproveItemId,
  LocationCampaignImproveItem,
  LocationCampaignImproveItemUpdate,
} from '@/types/campaign-improve';

function nullableText(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  return trimmed || null;
}

export const CAMPAIGN_IMPROVE_MIGRATION_HINT =
  'Apply migration 070_location_campaign_improve_items.sql in Supabase, then reload the API schema cache.';

export function isCampaignImproveTableMissingError(error: { message?: string; code?: string } | null | undefined): boolean {
  if (!error) return false;
  const message = error.message ?? '';
  return (
    error.code === 'PGRST205' ||
    message.includes('location_campaign_improve_items') ||
    message.includes('schema cache')
  );
}

export type CampaignImproveListResult = {
  items: LocationCampaignImproveItem[];
  persistenceAvailable: boolean;
};

export const locationCampaignImproveItemsService = {
  async listByLocationId(
    locationId: string,
    ctx: CampaignImproveDerivationContext
  ): Promise<CampaignImproveListResult> {
    try {
      const { data, error } = await supabase
        .from('location_campaign_improve_items')
        .select('*')
        .eq('location_id', locationId)
        .order('item_id', { ascending: true });

      if (error) {
        console.error('Error fetching campaign improve items:', error);
        return {
          items: mergeLocationCampaignImproveItems(locationId, [], ctx),
          persistenceAvailable: !isCampaignImproveTableMissingError(error),
        };
      }

      return {
        items: mergeLocationCampaignImproveItems(locationId, (data ?? []) as CampaignImproveItemRow[], ctx),
        persistenceAvailable: true,
      };
    } catch (error) {
      console.error('Error fetching campaign improve items:', error);
      return {
        items: mergeLocationCampaignImproveItems(locationId, [], ctx),
        persistenceAvailable: false,
      };
    }
  },

  async initializeChecklist(locationId: string, ctx: CampaignImproveDerivationContext): Promise<LocationCampaignImproveItem[]> {
    const { items: existing, persistenceAvailable } = await this.listByLocationId(locationId, ctx);
    if (!persistenceAvailable) {
      throw new Error(CAMPAIGN_IMPROVE_MIGRATION_HINT);
    }
    const missing = existing.filter((item) => item.id === null);
    if (missing.length === 0) {
      return existing;
    }

    const payload = missing.map((item) => ({
      location_id: locationId,
      item_id: item.item_id,
      status: 'not_started' as const,
      owner: item.defaultOwner,
      notes: '',
    }));

    const { error } = await supabase.from('location_campaign_improve_items').insert(payload);
    if (error) {
      console.error('Error initializing campaign improve checklist:', error);
      if (isCampaignImproveTableMissingError(error)) {
        throw new Error(CAMPAIGN_IMPROVE_MIGRATION_HINT);
      }
      throw new Error(`Failed to initialize CAMPAIGN-IMPROVE checklist: ${error.message}`);
    }

    return (await this.listByLocationId(locationId, ctx)).items;
  },

  async upsertItem(
    locationId: string,
    itemId: CampaignImproveItemId,
    data: LocationCampaignImproveItemUpdate,
    ctx: CampaignImproveDerivationContext
  ): Promise<LocationCampaignImproveItem> {
    const { items: existingItems, persistenceAvailable } = await this.listByLocationId(locationId, ctx);
    if (!persistenceAvailable) {
      throw new Error(CAMPAIGN_IMPROVE_MIGRATION_HINT);
    }
    const existing = existingItems.find((item) => item.item_id === itemId);
    if (!existing) {
      throw new Error(`Unknown campaign improve item: ${itemId}`);
    }

    const nextStatus = data.status ?? existing.status;
    const nextOwner = data.owner !== undefined ? nullableText(data.owner) : nullableText(existing.owner);
    const nextNotes = data.notes !== undefined ? data.notes : existing.notes;
    const nextCompletedAt = resolveCompletedAtForCampaignImproveStatus(nextStatus, existing.completed_at);

    const payload = {
      location_id: locationId,
      item_id: itemId,
      status: nextStatus,
      owner: nextOwner,
      completed_at: nextCompletedAt,
      notes: nextNotes,
    };

    const { error } = await supabase
      .from('location_campaign_improve_items')
      .upsert(payload, { onConflict: 'location_id,item_id' })
      .select()
      .single();

    if (error) {
      console.error('Error upserting campaign improve item:', error);
      throw new Error(`Failed to save ${itemId}: ${error.message}`);
    }

    return (await this.listByLocationId(locationId, ctx)).items.find((item) => item.item_id === itemId)!;
  },
};
